import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { loginWithPassword, getUserById } from '../repositories/user/userRepo';
import { Result } from '../models/result';
import useAuthStore from './authStore';
import useSchoolStore from './schoolStore';
/**
 * Creates a sanitized user profile with only necessary, non-sensitive data
 * This prevents sensitive information like passwords from being stored in localStorage
 * 
 * @param {User} user - The full user object
 * @returns {Object} Sanitized user profile safe for persistence
 */
const createSanitizedUserProfile = (user) => {
  if (!user) return null;
  
  return {
    userId: user.userId,
    name: user.name,
    admissionNo: user.admissionNo,
    class: user.class,
    sec: user.sec,
    profileUrl: user.profileUrl,
    schoolId: user.schoolId,
    // Explicitly excluding: password, student/teacher detailed data, etc.
  };
};

/**
 * Creates a public user profile safe for external services like AI
 * Contains even less information than the sanitized profile
 * 
 * @param {Object} userProfile - Sanitized user profile
 * @returns {Object} Public user profile
 */
const createPublicUserProfile = (userProfile) => {
  if (!userProfile) return null;
  
  return {
    userId: userProfile.userId,
    name: userProfile.name,
    schoolId: userProfile.schoolId,
    roleToken: userProfile.roleToken,
    // Only essential data for external services
  };
};

// User fields cleared when a login fails or the user logs out
const loggedOutUserState = {
  currentUser: null,
  userProfile: null,
  isUserLoggedIn: false,
};

const useUserStore = create(
  persist(
    (set, get) => ({
      // State
      currentUser: null, // Now stores full user object (in memory only)
      userProfile: null, // Sanitized profile for UI (persisted)
      isUserLoggedIn: false,
      isLoading: false,
      loginError: null,
      logoutError: null,
      schoolId: null,
      
      // Actions
      
      /**
       * Logs in a user through the ai-service login (userRepo.loginWithPassword),
       * signs Firebase Auth in with the returned custom token (useAuthStore.signInWithCustomToken),
       * then loads the user document with userRepo.getUserById.
       * 
       * @param {string} schoolId - The school ID
       * @param {string} username - The username
       * @param {string} password - The password
       * @returns {Promise<Result>} Result object with user data or error
       */
      loginUser: async (schoolId, username, password) => {
        if (!schoolId || !username || !password) {
          const errorResult = new Result({
            message: "School ID, username, and password are required.",
            code: "INVALID_INPUT"
          });
          set({ loginError: errorResult });
          return errorResult;
        }

        set({ isLoading: true, loginError: null });

        const loginResult = await loginWithPassword({ schoolId, username, password });
        if (!loginResult.success) {
          set({ ...loggedOutUserState, isLoading: false, loginError: loginResult });
          return loginResult;
        }

        const { customToken, userId } = loginResult.value;
        const signInResult = await useAuthStore.getState().signInWithCustomToken(customToken);
        if (!signInResult.success) {
          set({ ...loggedOutUserState, isLoading: false, loginError: signInResult });
          return signInResult;
        }

        // Read after sign-in: Firestore rules need the signed-in user
        const userFetchResult = await getUserById(schoolId, userId);
        if (!userFetchResult.success) {
          // Don't leave Firebase signed in without a dashboard user
          await useAuthStore.getState().signOut();
          set({ ...loggedOutUserState, isLoading: false, loginError: userFetchResult });
          return userFetchResult;
        }

        const fullUser = userFetchResult.value;
        const sanitizedProfile = createSanitizedUserProfile(fullUser);

        // Login successful - store full user in memory, sanitized profile for persistence
        set({
          currentUser: fullUser, // Full user data (memory only)
          userProfile: sanitizedProfile, // Sanitized data (persisted)
          isUserLoggedIn: true,
          isLoading: false,
          loginError: null,
          schoolId: schoolId
        });

        return new Result({
          value: sanitizedProfile, // Return sanitized data
          message: "User logged in successfully."
        });
      },

      /**
       * Logs out the current user and signs Firebase Auth out
       * (useAuthStore.signOut), since the Firebase session is the login.
       * 
       * @returns {Promise<Result>} Result object indicating success or failure
       */
      logoutUser: async () => {
        try {
          set({ isLoading: true });

          // Clear user state
          set({
            ...loggedOutUserState,
            isLoading: false,
            loginError: null,
            logoutError: null
          });

          const signOutResult = await useAuthStore.getState().signOut();
          if (!signOutResult.success) {
            const errorResult = new Result({
              message: `Logout partially failed: ${signOutResult.message}`,
              code: "PARTIAL_LOGOUT_ERROR"
            });
            set({ logoutError: errorResult });
            return errorResult;
          }

          return new Result({
            value: true,
            message: "User logged out successfully."
          });
        } catch (error) {
          const errorResult = new Result({
            message: `Logout failed: ${error.message}`,
            code: "LOGOUT_ERROR"
          });
          set({ isLoading: false, logoutError: errorResult });
          return errorResult;
        }
      },

      /**
       * Checks if user session is still valid
       * Verifies both user login state and Firebase Auth state
       * 
       * @returns {Result} Result object indicating session validity
       */
      validateSession: () => {
        const { userProfile, isUserLoggedIn } = get();
        const authState = useAuthStore.getState();

        if (!authState.isAuthenticated) {
          const errorResult = new Result({
            message: "Session expired. Please authenticate again.",
            code: "SESSION_EXPIRED"
          });
          set({
            currentUser: null,
            userProfile: null,
            isUserLoggedIn: false,
            loginError: errorResult,
            logoutError: errorResult
          });
          
          return errorResult;
        }

        if (!isUserLoggedIn || !userProfile) {
          const errorResult = new Result({
            message: "User not logged in.",
            code: "NOT_LOGGED_IN"
          });
          return errorResult;
        }

        return new Result({
          value: userProfile,
          message: "Session is valid."
        });
      },

      /**
       * Updates user profile data (only non-sensitive fields)
       * 
       * @param {Object} userData - Updated user data
       * @returns {Result} Result object indicating success or failure
       */
      updateUserProfile: (userData) => {
        const { userProfile } = get();
        
        if (!userProfile) {
          const errorResult = new Result({
            message: "No user logged in to update.",
            code: "NOT_LOGGED_IN"
          });
          set({ loginError: errorResult });
          return errorResult;
        }

        try {
          // Only allow updating safe, non-sensitive fields
          const allowedFields = ['name', 'profileUrl'];
          const sanitizedUpdate = {};
          
          allowedFields.forEach(field => {
            if (userData[field] !== undefined) {
              sanitizedUpdate[field] = userData[field];
            }
          });

          const updatedProfile = { ...userProfile, ...sanitizedUpdate };
          set({ userProfile: updatedProfile, loginError: null });

          return new Result({
            value: updatedProfile,
            message: "User profile updated successfully."
          });
        } catch (error) {
          const errorResult = new Result({
            message: `Failed to update user profile: ${error.message}`,
            code: "UPDATE_PROFILE_ERROR"
          });
          set({ loginError: errorResult });
          return errorResult;
        }
      },

      /**
       * Refreshes user profile from server silently
       * Used to fetch the latest user data on app startup or when needed
       * 
       * @returns {Promise<Result>} Result object indicating success or failure
       */
      refreshUserProfile: async () => {
        const { userProfile, isUserLoggedIn, schoolId } = get();
        
        if (!isUserLoggedIn || !userProfile || !userProfile.userId) {
          return new Result({
            message: "No user logged in to refresh.",
            code: "NOT_LOGGED_IN"
          });
        }

        try {
        

          // Fetch latest user data from server
          const userFetchResult = await getUserById(schoolId || userProfile.schoolId, userProfile.userId);
          
          if (userFetchResult.success) {
            const fullUser = userFetchResult.value;
            const sanitizedProfile = createSanitizedUserProfile(fullUser);
            
            // Update both full user data and sanitized profile
            set({
              currentUser: fullUser, // Full user data (memory only)
              userProfile: sanitizedProfile, // Sanitized data (persisted)
              loginError: null
            });
            
            // console.log(`currentUser: ${JSON.stringify(get().currentUser)}`)
            return new Result({
              value: sanitizedProfile,
              message: "User profile refreshed successfully."
            });
          } else {
            // Don't update loginError for silent refresh failures unless it's a critical error
            if (userFetchResult.code === "USER_NOT_FOUND") {
              // User might have been deleted - log them out
              set({
                currentUser: null,
                userProfile: null,
                isUserLoggedIn: false,
                loginError: userFetchResult
              });
            }
            return userFetchResult;
          }
        } catch (error) {
          const errorResult = new Result({
            message: `Failed to refresh user profile: ${error.message}`,
            code: "REFRESH_PROFILE_ERROR"
          });
          set({ isLoading: false });
          // Don't set loginError for network/temporary failures
          return errorResult;
        }
      },

      /**
       * Gets current user information
       * 
       * @returns {Result} Result object with user data or error
       */
      getCurrentUser: async () => {
        const { userProfile, isUserLoggedIn } = get();
        const { schoolId } = useSchoolStore.getState();

        if (!isUserLoggedIn || !userProfile) {
          return new Result({
            message: "No user currently logged in.",
            code: "NOT_LOGGED_IN"
          });
        }
        set({isLoading: true, loginError: null});
        const userFetchResult = await getUserById(schoolId || userProfile.schoolId, userProfile.userId);
        if(userFetchResult.success){
          const fullUser = userFetchResult.value;
          const sanitizedProfile = createSanitizedUserProfile(fullUser);
          set({
            currentUser: fullUser,
            userProfile: sanitizedProfile,
            isLoading: false,
            loginError: null
          });
        } else {
          set({isLoading: false, loginError: userFetchResult});
        }
      },

      /**
       * Gets public user profile for external services (AI, etc.)
       * Contains minimal, safe information
       * 
       * @returns {Result} Result object with public user data or error
       */
      getPublicUserProfile: () => {
        const { userProfile, isUserLoggedIn } = get();
        
        if (!isUserLoggedIn || !userProfile) {
          return new Result({
            message: "No user currently logged in.",
            code: "NOT_LOGGED_IN"
          });
        }

        const publicProfile = createPublicUserProfile(userProfile);
        // console.log(`publicProfile: ${JSON.stringify(publicProfile)}`);
        return new Result({
          value: publicProfile,
          message: "Public user profile retrieved successfully."
        });
      },

      /**
       * Gets the full user object (available only in memory during session)
       * Should be used sparingly and only when absolutely necessary
       * 
       * @returns {Result} Result object with full user data or error
       */
      getFullUserData: () => {
        const { currentUser, isUserLoggedIn } = get();
        
        if (!isUserLoggedIn || !currentUser) {
          return new Result({
            message: "No user currently logged in or full data not available.",
            code: "NOT_LOGGED_IN"
          });
        }

        return new Result({
          value: currentUser,
          message: "Full user data retrieved successfully."
        });
      },

      /**
       * Clears any login errors
       */
      clearLoginError: () => {
        set({ loginError: null });
        return new Result({ value: true });
      },

      /**
       * Clears any login errors (alias for clearLoginError for consistency)
       */
      clearError: () => {
        set({ loginError: null });
        return new Result({ value: true });
      },

      /**
       * Gets the current login error (complete Result object)
       * 
       * @returns {Result|null} The current error Result object or null
       */
      getLoginError: () => {
        const { loginError } = get();
        return loginError;
      },

      /**
       * Checks if user has specific role or permission
       * 
       * @param {string} role - Role to check for
       * @returns {Result} Result object indicating if user has role
       */
      hasRole: (role) => {
        const { userProfile } = get();
        
        if (!userProfile) {
          return new Result({
            value: false,
            message: "No user logged in.",
            code: "NOT_LOGGED_IN"
          });
        }

        const hasRole = userProfile.role === role || 
                       (userProfile.roles && userProfile.roles.includes(role));

        return new Result({
          value: hasRole,
          message: hasRole ? "User has required role." : "User does not have required role.",
          code: hasRole ? "ROLE_GRANTED" : "ROLE_DENIED"
        });
      },

      /**
       * Resets the entire user store to initial state
       */
      resetUserStore: () => {
        set({
          currentUser: null,
          userProfile: null,
          isUserLoggedIn: false,
          isLoading: false,
          loginError: null,
          schoolId: null,
          logoutError: null
        });
        return new Result({ value: true });
      }
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        // SECURITY: Only persist non-sensitive, sanitized data
        userProfile: state.userProfile, // Sanitized profile only
        isUserLoggedIn: state.isUserLoggedIn,
        schoolId: state.schoolId,
        // IMPORTANT: currentUser is NOT persisted (contains sensitive data like passwords)
        // loginError is NOT persisted (should be fresh on each session)
      })
    }
  )
);

export default useUserStore;
